import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { hotjar } from 'react-hotjar';
import Clarity from '@microsoft/clarity';

const HOTJAR_SITE_ID = 1184490;
const HOTJAR_SNIPPET_VERSION = 6;

/* Not a secret: the tag is fetched by the browser, so this ends up in the
   bundle either way. It lives here rather than in an env var so the build has
   one less thing that can be missing. */
const CLARITY_PROJECT_ID = 'xvzwertzaf';

/**
 * Client-side navigation does not reload the document, so three things that
 * browsers normally do for free have to be done by hand:
 *
 *  1. reset scroll position,
 *  2. move focus somewhere sensible (otherwise it stays on the old link and
 *     keyboard users carry on from wherever they were),
 *  3. tell analytics the page changed.
 *
 * This is also where the analytics tags are started, since it is the one hook
 * that already runs for the lifetime of the app.
 *
 * @param {React.RefObject<HTMLElement>} focusTarget Element to focus after a navigation.
 */
export function useRouteChange(focusTarget) {
  const { pathname } = useLocation();
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!hotjar.initialized()) {
      hotjar.initialize({ id: HOTJAR_SITE_ID, sv: HOTJAR_SNIPPET_VERSION });
    }

    // Only on the deployed site: a dev server session is not a visitor, and
    // recordings of work in progress are noise in the dashboard. Clarity's own
    // injector is idempotent (it checks for its script tag), so a re-run during
    // a hot reload cannot load the tag twice.
    if (import.meta.env.PROD) {
      Clarity.init(CLARITY_PROJECT_ID);
    }
  }, []);

  useEffect(() => {
    // Hotjar has to be told; Clarity patches the History API itself and picks
    // up client-side navigations without help, so there is nothing to call.
    if (hotjar.initialized()) {
      hotjar.stateChange(pathname);
    }

    // Don't steal focus or fight the browser on the very first paint.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
    focusTarget?.current?.focus({ preventScroll: true });
  }, [pathname, focusTarget]);

  return pathname;
}

export default useRouteChange;
